import React, { useEffect, useState } from 'react';
import { Copy, HelpCircle } from 'lucide-react';
import { MCP_PUBLIC_URL } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import type { GatewayApiKey, McpClient, McpEndpoint } from '../governedMcpTypes';
import { GatewayDrawer, ghostBtn, labelClass, panelClass } from '../components/GatewayUi';

type HelpClient = 'claude' | 'chatgpt' | 'copilot' | 'custom';

const CLIENT_TABS: { id: HelpClient; label: string }[] = [
  { id: 'claude', label: 'Claude' },
  { id: 'chatgpt', label: 'ChatGPT' },
  { id: 'copilot', label: 'Microsoft Copilot' },
  { id: 'custom', label: 'Custom MCP client' },
];

function bearerValue(jwt: string): string {
  return jwt ? `Bearer ${jwt}` : '';
}

function CopyLine({
  label,
  value,
  isLight,
  mono,
  pre,
  onCopy,
}: {
  label: string;
  value: string;
  isLight: boolean;
  mono?: boolean;
  pre?: boolean;
  onCopy: (value: string, label: string) => void;
}) {
  return (
    <div>
      <p className={labelClass(isLight)}>{label}</p>
      <div className="flex gap-2 items-start">
        <p
          className={`flex-1 min-w-0 rounded-xl border px-3 py-2.5 text-sm ${
            pre ? 'whitespace-pre-wrap max-h-56 overflow-y-auto' : 'break-all'
          } ${mono ? 'font-mono' : ''} ${isLight ? 'border-neutral-200 bg-white text-neutral-900' : 'border-neutral-700 bg-neutral-900 text-white'}`}
        >
          {value || '—'}
        </p>
        <button type="button" className={ghostBtn(isLight)} disabled={!value} onClick={() => onCopy(value, label)}>
          <Copy size={14} />
        </button>
      </div>
    </div>
  );
}

function StepList({ items, isLight }: { items: string[]; isLight: boolean }) {
  return (
    <ol className={`list-decimal pl-5 space-y-2 text-sm ${isLight ? 'text-neutral-700' : 'text-neutral-300'}`}>
      {items.map((item) => (
        <li key={item} className="leading-relaxed">
          {item}
        </li>
      ))}
    </ol>
  );
}

export default function ApiKeySetupHelpDrawer({
  open,
  item,
  endpoint,
  boundClient,
  isLight,
  onClose,
  onToast,
}: {
  open: boolean;
  item: GatewayApiKey | null;
  endpoint: McpEndpoint | null;
  boundClient: McpClient | null;
  isLight: boolean;
  onClose: () => void;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const [client, setClient] = useState<HelpClient>('claude');

  useEffect(() => {
    if (!open) return;
    setClient('claude');
    logGovernedMcp('app', 'api_key_help_open', { id: item?.id, clientId: item?.clientId, endpointId: item?.endpointId });
  }, [open, item]);

  const mcpUrl = endpoint?.url || MCP_PUBLIC_URL;
  const jwt = item?.secret || '';
  const authHeader = bearerValue(jwt);
  const boundClientName = boundClient?.name || '';
  const slugName = (item?.name || boundClient?.name || endpoint?.name || 'selfic-mcp').replace(/\s+/g, '-').toLowerCase();
  const claudeCode = mcpUrl && jwt
    ? `claude mcp add --transport http ${slugName} ${mcpUrl} --header "Authorization: Bearer ${jwt}"`
    : '';
  const customJson = mcpUrl
    ? JSON.stringify(
        {
          mcpServers: {
            [slugName]: {
              type: 'http',
              url: mcpUrl,
              headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
            },
          },
        },
        null,
        2
      )
    : '';
  const claudeDesktopJson = mcpUrl
    ? JSON.stringify(
        {
          mcpServers: {
            [slugName]: {
              command: 'npx',
              args: [
                '-y',
                'mcp-remote',
                mcpUrl,
                '--header',
                'Authorization:${AUTH_HEADER}',
              ],
              env: jwt ? { AUTH_HEADER: `Bearer ${jwt}` } : {},
            },
          },
        },
        null,
        2
      )
    : '';
  const claudeDesktopWin = '%APPDATA%\\Claude\\claude_desktop_config.json';
  const claudeDesktopMac = '~/Library/Application Support/Claude/claude_desktop_config.json';

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      onToast('success', `${label} copied`);
      logGovernedMcp('app', 'api_key_help_copied', { label, client });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'api_key_help_copy_failed', { label, client });
    }
  };

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const title = isLight ? 'text-neutral-900' : 'text-white';
  const rail = (active: boolean) =>
    `w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
      active
        ? isLight
          ? 'bg-indigo-50 text-indigo-700 font-medium'
          : 'bg-indigo-500/15 text-indigo-300 font-medium'
        : isLight
          ? 'text-neutral-600 hover:bg-neutral-50'
          : 'text-neutral-400 hover:bg-neutral-900'
    }`;

  return (
    <GatewayDrawer
      open={open && Boolean(item)}
      eyebrow="Governed MCP / API Keys"
      icon={<HelpCircle size={18} className="text-indigo-500" />}
      title="Configure this MCP"
      subtitle="Use this card’s MCP URL and JWT in Claude, ChatGPT, Copilot, or any Streamable HTTP client."
      isLight={isLight}
      size="xl"
      onClose={onClose}
      footer={
        <button type="button" className={ghostBtn(isLight)} onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <CopyLine label="MCP URL" value={mcpUrl} isLight={isLight} mono onCopy={copy} />
        <CopyLine label="Authorization header" value={authHeader} isLight={isLight} mono onCopy={copy} />
      </div>
      <p className={`text-xs mb-5 ${muted}`}>
        Transport: Streamable HTTP · {MCP_PUBLIC_URL}
        {boundClientName ? ` · Client: ${boundClientName}` : ' · Unassigned client'}
        {item?.expiresAt ? ` · JWT expires ${new Date(item.expiresAt).toLocaleString()}` : ''}
      </p>

      <div className="flex gap-5 items-start">
        <nav className={`w-48 shrink-0 rounded-xl border p-2 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`} aria-label="Client setup">
          {CLIENT_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={rail(client === tab.id)}
              onClick={() => {
                setClient(tab.id);
                logGovernedMcp('app', 'api_key_help_tab', { client: tab.id });
              }}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="flex-1 min-w-0 space-y-4">
          {client === 'claude' ? (
            <>
              <h3 className={`text-sm font-semibold ${title}`}>Add from Claude Connectors</h3>
              <p className={`text-sm ${muted}`}>
                Team / Enterprise owners: Organization settings → Connectors → Add → Custom. If Claude asks for type, choose Web.
              </p>
              <p className={`text-sm ${muted}`}>Pro / Max: Customize → Connectors → Add custom connector.</p>
              <StepList
                isLight={isLight}
                items={[
                  `Name the connector “${item?.name || boundClient?.name || endpoint?.name || 'Selfic MCP'}”.`,
                  'Paste the MCP URL from this card. Do not add a query string.',
                  'Skip OAuth Advanced settings. OAuth owns Authorization and conflicts with this Universal Token.',
                  'Open Request headers. Add Authorization. Enter the full value Bearer, a space, then this card’s JWT.',
                  'Click Add. Members then go to Customize → Connectors and click Connect on this custom connector.',
                ]}
              />
              {claudeCode ? (
                <div>
                  <p className={`text-sm font-medium mb-2 ${title}`}>Claude Code</p>
                  <CopyLine label="Command" value={claudeCode} isLight={isLight} mono onCopy={copy} />
                </div>
              ) : null}

              <div className={`${panelClass(isLight)} p-4 space-y-4`}>
                <div>
                  <h3 className={`text-sm font-semibold ${title}`}>Developer mode</h3>
                  <p className={`text-sm mt-1 ${muted}`}>
                    Claude Desktop → Settings → Developer → Edit Config. Merge the snippet into <span className="font-mono">mcpServers</span>. Do not replace the rest of the file. Fully quit Claude Desktop and reopen.
                  </p>
                </div>
                <div className="grid gap-3">
                  <CopyLine label="Windows" value={claudeDesktopWin} isLight={isLight} mono onCopy={copy} />
                  <CopyLine label="macOS" value={claudeDesktopMac} isLight={isLight} mono onCopy={copy} />
                </div>
                {claudeDesktopJson ? (
                  <CopyLine
                    label="claude_desktop_config.json"
                    value={claudeDesktopJson}
                    isLight={isLight}
                    mono
                    pre
                    onCopy={copy}
                  />
                ) : null}
                <p className={`text-xs ${muted}`}>
                  Desktop only launches local processes, so this uses <span className="font-mono">npx mcp-remote</span> as a bridge. Node.js is required. Claude Code can use the HTTP file instead: project <span className="font-mono">.mcp.json</span>, or user <span className="font-mono">~/.claude.json</span>.
                </p>
                {customJson ? (
                  <CopyLine label="Claude Code .mcp.json" value={customJson} isLight={isLight} mono pre onCopy={copy} />
                ) : null}
              </div>
            </>
          ) : null}

          {client === 'chatgpt' ? (
            <>
              <h3 className={`text-sm font-semibold ${title}`}>Add in ChatGPT</h3>
              <StepList
                isLight={isLight}
                items={[
                  'Open ChatGPT Settings → Apps & Connectors. Enable Developer mode under Advanced if the Create action is hidden.',
                  'Click Create. Enter a name and paste the MCP URL from this card.',
                  'Set Authentication to Token or Custom. Use header Authorization with Bearer, a space, then this JWT.',
                  'Save. In a chat, open + and enable this connector for the conversation.',
                ]}
              />
              <p className={`text-sm ${muted}`}>
                Some ChatGPT workspace builds only offer OAuth. If Token / custom headers are missing, use Claude or a custom MCP client until OAuth is enabled on this gateway.
              </p>
            </>
          ) : null}

          {client === 'copilot' ? (
            <>
              <h3 className={`text-sm font-semibold ${title}`}>Add in Microsoft Copilot</h3>
              <StepList
                isLight={isLight}
                items={[
                  'In Copilot Studio, create or open a custom connector / MCP connection.',
                  'Set the server URL to the MCP URL on this card. Transport is Streamable HTTP.',
                  'Add an Authorization request header with Bearer, a space, then this JWT.',
                  'Publish the connection to the Copilot that should call Selfic tools.',
                ]}
              />
            </>
          ) : null}

          {client === 'custom' ? (
            <>
              <h3 className={`text-sm font-semibold ${title}`}>Any Streamable HTTP client</h3>
              <p className={`text-sm ${muted}`}>
                Point the client at this URL and send the Authorization header on every request. Cursor and similar tools accept the JSON below.
              </p>
              {customJson ? <CopyLine label="Client config" value={customJson} isLight={isLight} mono onCopy={copy} /> : null}
            </>
          ) : null}
        </div>
      </div>
    </GatewayDrawer>
  );
}
